import type { Activity, Condition, CycleRecord, DailyRecord, HealthMetric, Insight, PainEntry, User } from '../types/health';
import type { LineChartPoint } from '../components/charts/LineChart';

export const mockUser: User = {
  id: 'user_sharlene',
  name: 'Sharlene',
  timezone: 'Asia/Seoul',
  createdAt: '2026-10-07T00:00:00.000Z',
};

export const todayRecord: DailyRecord = {
  id: 'daily_2026_10_07',
  userId: mockUser.id,
  date: '2026-10-07',
  createdAt: '2026-10-07T00:00:00.000Z',
  updatedAt: '2026-10-07T00:00:00.000Z',
};

export const latestMetrics: HealthMetric[] = [];

export const healthSyncStatus: Array<{
  id: string;
  label: string;
  status: 'connected' | 'manual' | 'planned';
  lastSync?: string;
  metricTypes: HealthMetric['type'][];
  note: string;
}> = [
  {
    id: 'sync_apple_health',
    label: 'Apple Health',
    status: 'planned',
    metricTypes: [],
    note: 'Apple Health is not importing real samples yet, so Body will not show health values here.',
  },
  {
    id: 'sync_manual_health',
    label: 'Manual health log',
    status: 'manual',
    metricTypes: [],
    note: 'Pain, symptoms, and injury context remain editable even without Apple Health.',
  },
  {
    id: 'sync_ios_bridge',
    label: 'iOS HealthKit bridge',
    status: 'planned',
    metricTypes: ['heart_rate', 'active_energy', 'walking_running_distance'],
    note: 'The future iOS layer will push HealthKit samples into the Body API after permission is granted.',
  },
];

export const cycleRecords: CycleRecord[] = [];

export const activities: Activity[] = [
  {
    id: 'activity_upper_20261007',
    userId: mockUser.id,
    type: 'strength',
    title: 'Upper Body',
    startTime: '2026-10-07T18:30:00+09:00',
    durationMinutes: 45,
    rpe: 5,
    source: 'manual',
    notes: 'Moderate',
    createdAt: '2026-10-07T12:00:00+09:00',
    updatedAt: '2026-10-07T12:00:00+09:00',
  },
  {
    id: 'activity_walk_20261006',
    userId: mockUser.id,
    type: 'walking',
    title: 'Short Walk',
    startTime: '2026-10-06T17:10:00+09:00',
    durationMinutes: 18,
    source: 'apple_health',
    sourceId: 'ah_workout_20261006_walk',
    createdAt: '2026-10-06T17:30:00+09:00',
    updatedAt: '2026-10-06T17:30:00+09:00',
  },
];

export const conditions: Condition[] = [
  {
    id: 'condition_acl_left',
    userId: mockUser.id,
    name: 'ACL Injury',
    type: 'injury',
    side: 'left',
    startDate: '2026-08-18',
    status: 'active',
    notes: 'Timeline-ready placeholder for injury, surgery, rehabilitation, and recovery.',
    createdAt: '2026-10-07T00:00:00.000Z',
    updatedAt: '2026-10-07T00:00:00.000Z',
  },
];

export const painEntries: PainEntry[] = [
  {
    id: 'pain_20261007_knee',
    userId: mockUser.id,
    conditionId: 'condition_acl_left',
    date: '2026-10-07',
    location: 'knee',
    side: 'left',
    painScore: 2,
    painType: 'ache',
    trigger: 'stairs',
    notes: 'Mild ache after stairs. No swelling reported.',
    source: 'manual',
    createdAt: '2026-10-07T10:00:00+09:00',
    updatedAt: '2026-10-07T10:00:00+09:00',
  },
];

export const recoveryPlan = {
  conditionId: 'condition_acl_left',
  status: 'train_with_limits',
  statusLabel: 'Train with limits',
  readinessImpact: 'low',
  recommendation: 'Upper body training is fine today. Keep lower-body work controlled and avoid impact until knee pain is stable for 7 days.',
  focus: ['Knee pain is low', 'Sleep is above 7 hours', 'HRV is steady'],
  avoid: ['Jumping', 'Max-effort squats', 'Unplanned running volume'],
  nextCheckIn: '2026-10-08',
};

export const insights: Insight[] = [
  {
    id: 'insight_sleep_rpe',
    userId: mockUser.id,
    type: 'recovery',
    title: 'Sleep and training effort move together',
    description: 'When sleep is above 7 hours, your average workout RPE is lower.',
    confidence: 0.72,
    relatedMetrics: ['sleep_duration'],
    startDate: '2026-09-21',
    endDate: '2026-10-07',
    createdAt: '2026-10-07T00:00:00.000Z',
  },
  {
    id: 'insight_sleep_streak',
    userId: mockUser.id,
    type: 'recovery',
    title: 'Sleep has improved for 3 consecutive days',
    description: 'Your sleep duration trend is moving in the right direction this week.',
    confidence: 0.86,
    relatedMetrics: ['sleep_duration', 'hrv'],
    createdAt: '2026-10-07T00:00:00.000Z',
  },
];

export const weightTrend: LineChartPoint[] = [];
